import { sum } from "lodash";
import { logError } from "Utils/logger";

import { CONTRIBUTION_TYPES } from "Common/Constants";

import { getPricingSegmentAncillary, getPricingSegmentMedical } from "./Utils";

// Company Cost Totals
function companyPercentContributionForPlan(employeeCost, employee, planType, contribution) {
  let cost = 0;

  cost += employeeCost?.self_cost * (contribution.employeePercentage / 100);
  if (employee.spouse_enrollment && employee.spouse_enrollment[planType]) {
    cost += employeeCost?.spouse_cost * (contribution.dependentPercentage / 100) || 0;
  }
  employee.children_enrollment?.map((enrollment, childIndex) => {
    if (enrollment[planType] && employeeCost?.child_cost) {
      cost += employeeCost?.child_cost[childIndex] * (contribution.dependentPercentage / 100) || 0;
    }
  });
  return cost;
}

// Base logic is loop through employees and sum the min price
// between company contribution and employee plan cost
export const calculateCompanyCost = (employees, medicalPlans, dentalPlan, visionPlan, contributionType, contributions, lifePlan) => {
  let totalCost = 0;
  if (contributionType == CONTRIBUTION_TYPES.percentage || contributionType === undefined) {
    employees.forEach((employee, index) => {
      const contribution = contributions.find((contribution) => contribution.role === employee.role) || contributions[0];
      if (employee.is_enrolling && employee.age !== null) {
        if (employee.selected_medical_plan) {
          const medicalPlan = medicalPlans.find((plan) => plan.id === employee.selected_medical_plan);

          totalCost += companyPercentContributionForPlan(medicalPlan.employee_costs[index], employee, "is_enrolling", contribution);
        } else {
          totalCost += companyPercentContributionForPlan(medicalPlans[0].employee_costs[index], employee, "is_enrolling", contribution);
        }
      }
      if (dentalPlan?.employee_costs && employee.is_enrolling_dental && employee.age !== null) {
        totalCost += companyPercentContributionForPlan(dentalPlan.employee_costs[index], employee, "is_enrolling_dental", contribution);
      }
      if (visionPlan?.employee_costs && employee.is_enrolling_vision && employee.age !== null) {
        totalCost += companyPercentContributionForPlan(visionPlan.employee_costs[index], employee, "is_enrolling_vision", contribution);
      }
    });
  } else if (contributionType == CONTRIBUTION_TYPES.percent_of_base_plan) {
    employees.forEach((employee, index) => {
      const contribution = contributions.find((contribution) => contribution.role === employee.role) || contributions[0];
      if (employee.is_enrolling && employee.age !== null) {
        totalCost += companyPercentContributionForPlan(medicalPlans[0].employee_costs[index], employee, "is_enrolling", contribution);
      }
      if (dentalPlan && employee.is_enrolling_dental && employee.age !== null) {
        totalCost += companyPercentContributionForPlan(dentalPlan.employee_costs[index], employee, "is_enrolling_dental", contribution);
      }
      if (visionPlan && employee.is_enrolling_vision && employee.age !== null) {
        totalCost += companyPercentContributionForPlan(visionPlan.employee_costs[index], employee, "is_enrolling_vision", contribution);
      }
    });
  } else if (contributionType == CONTRIBUTION_TYPES.fixed) {
    employees.map((employee, index) => {
      if (!employee.is_enrolling || employee.age === null) {
        return;
      }
      const contribution = contributions.find((contribution) => contribution.role === employee.role) || contributions[0];

      // Medical Plan Calculations
      // For fixed contribution we only calculate the contribution
      // towards medical so no need to calculate company costs
      // for dental and vision
      let employeeCost = medicalPlans[0].employee_costs[index];
      if (employee.selected_medical_plan) {
        const medicalPlan = medicalPlans.find((plan) => plan.id === employee.selected_medical_plan);

        employeeCost = medicalPlan.employee_costs[index];
      }

      let dependentsPlanCost = 0;
      if (employee.spouse_enrollment && employee.spouse_enrollment.is_enrolling) {
        dependentsPlanCost += employeeCost?.spouse_cost;
      }
      employee.children_enrollment?.map((enrollment, childIndex) => {
        if (enrollment.is_enrolling && employeeCost?.child_cost) {
          dependentsPlanCost += employeeCost?.child_cost[childIndex];
        }
      });

      totalCost += Math.min(employeeCost?.self_cost, contribution.employeeFixedAmount) + Math.min(dependentsPlanCost, contribution.dependentFixedAmount);
    });
  }
  if (lifePlan && lifePlan.total_cost) {
    const fullTimeEmployeesCost = employees
      .filter((employee) => employee.full_time)
      .reduce((cost, _employee, _index) => {
        const perEmployeeLifeCost = lifePlan.total_cost / employees.length;
        return cost + perEmployeeLifeCost;
      }, 0);
    totalCost += fullTimeEmployeesCost;
  }
  return totalCost;
};

export const calculateEmployeesCost = (employees, medicalPlans, dentalPlan, visionPlan, contributionType, contributions) =>
  calculateTotalCost(employees, medicalPlans, dentalPlan, visionPlan) -
  calculateCompanyCost(employees, medicalPlans, dentalPlan, visionPlan, contributionType, contributions);

const employeeCostForPlan = (employeeCost, employee, planType, pricingType) => {
  let cost = 0;
  cost += employeeCost?.self_cost;
  if (employee.spouse_enrollment && employee.spouse_enrollment[planType]) {
    cost += employeeCost?.spouse_cost || 0;
  }
  const hasChildrenEnrolling = employee.children_enrollment?.some((enrollment) => enrollment[planType]);

  if (pricingType === "segment" && hasChildrenEnrolling) {
    cost += sum(employeeCost?.child_cost || []);
  } else {
    employee.children_enrollment?.map((enrollment, childIndex) => {
      if (enrollment[planType] && employeeCost?.child_cost) {
        cost += employeeCost?.child_cost[childIndex] || 0;
      }
    });
  }

  return cost;
};

export const calculateTotalCost = (employees, medicalPlans, dentalPlan, visionPlan, lifePlan) => {
  let totalCost = 0;
  employees.map((employee, index) => {
    if (medicalPlans.length > 0 && employee.is_enrolling && employee.age !== null) {
      if (employee.selected_medical_plan) {
        const medicalPlan = medicalPlans.find((plan) => plan.id === employee.selected_medical_plan);

        totalCost += employeeCostForPlan(medicalPlan.employee_costs[index], employee, "is_enrolling", medicalPlan.pricing_type);
      } else {
        totalCost += employeeCostForPlan(medicalPlans[0].employee_costs[index], employee, "is_enrolling", medicalPlans[0].pricing_type);
      }
    }
    if (dentalPlan?.employee_costs && employee.is_enrolling_dental && employee.age !== null) {
      totalCost += employeeCostForPlan(dentalPlan.employee_costs[index], employee, "is_enrolling_dental", dentalPlan.pricing_type);
    }
    if (visionPlan?.employee_costs && employee.is_enrolling_vision && employee.age !== null) {
      totalCost += employeeCostForPlan(visionPlan.employee_costs[index], employee, "is_enrolling_vision", visionPlan.pricing_type);
    }
  });
  if (lifePlan && lifePlan.total_cost) {
    totalCost += lifePlan.total_cost;
  }
  return totalCost;
};

// Individual Cost Totals
export const calculateIndividualCompanyCost = (
  contributionType,
  contribution,
  index,
  selectedPlan,
  medicalPlans,
  dentalPlan,
  visionPlan,
  employee,
  employeeLifeCost,
  disabilityCostForIndividual,
) => {
  if (contributionType == CONTRIBUTION_TYPES.percentage || contributionType === undefined) {
    let costs = 0;
    if (selectedPlan && employee.is_enrolling) {
      costs += (selectedPlan.employee_costs[index]?.self_cost || 0) * (contribution.employeePercentage / 100);
    }
    if (dentalPlan?.employee_costs && employee.is_enrolling_dental) {
      costs += (dentalPlan?.employee_costs[index]?.self_cost || 0) * (contribution.employeePercentage / 100);
    }
    if (visionPlan?.employee_costs && employee.is_enrolling_vision) {
      costs += (visionPlan?.employee_costs[index]?.self_cost || 0) * (contribution.employeePercentage / 100);
    }
    if (employeeLifeCost && employee.full_time) {
      costs += employeeLifeCost;
    }
    if (disabilityCostForIndividual) {
      costs += disabilityCostForIndividual;
    }
    return costs;
  }
  if (contributionType == CONTRIBUTION_TYPES.percent_of_base_plan) {
    let costs = 0;
    if (medicalPlans && medicalPlans.length > 0 && employee.is_enrolling) {
      costs += (medicalPlans[0].employee_costs[index]?.self_cost || 0) * (contribution.employeePercentage / 100);
    }
    if (dentalPlan?.employee_costs && employee.is_enrolling_dental) {
      costs += (dentalPlan?.employee_costs[index]?.self_cost || 0) * (contribution.employeePercentage / 100);
    }
    if (visionPlan?.employee_costs && employee.is_enrolling_vision) {
      costs += (visionPlan?.employee_costs[index]?.self_cost || 0) * (contribution.employeePercentage / 100);
    }
    if (employeeLifeCost && employee.full_time) {
      costs += employeeLifeCost;
    }
    if (disabilityCostForIndividual) {
      costs += disabilityCostForIndividual;
    }
    return costs;
  }
  if (contributionType == CONTRIBUTION_TYPES.fixed) {
    let costs = 0;
    if (employee.is_enrolling) {
      costs += Math.min(selectedPlan.employee_costs[index]?.self_cost || 0, parseInt(contribution.employeeFixedAmount || 0, 10));
    }
    if (employeeLifeCost && employee.full_time) {
      costs += employeeLifeCost;
    }
    if (disabilityCostForIndividual) {
      costs += disabilityCostForIndividual;
    }
    return costs;
  }
  return 0;
};

export const calculateIndividualEmployeeCost = (index, selectedPlan, contributionType, contribution, medicalPlans, dentalPlan, visionPlan, employee) =>
  calculateIndividualTotalCost(index, selectedPlan, dentalPlan, visionPlan, employee) -
  calculateIndividualCompanyCost(contributionType, contribution, index, selectedPlan, medicalPlans, dentalPlan, visionPlan, employee);

export const calculateIndividualTotalCost = (index, selectedPlan, dentalPlan, visionPlan, employee, employeeLifeCost, disabilityCostForIndividual) => {
  let costs = 0;
  if (selectedPlan && employee.is_enrolling) {
    costs += selectedPlan.employee_costs[index]?.self_cost || 0;
  }
  if (dentalPlan?.employee_costs && employee.is_enrolling_dental) {
    costs += dentalPlan?.employee_costs[index]?.self_cost || 0;
  }
  if (visionPlan?.employee_costs && employee.is_enrolling_vision) {
    costs += visionPlan?.employee_costs[index]?.self_cost || 0;
  }
  if (employeeLifeCost) {
    costs += employeeLifeCost;
  }
  if (disabilityCostForIndividual) {
    costs += disabilityCostForIndividual;
  }
  return costs;
};

// Dependent Costs
// Total cost for a dependent
export const findDependentCostforSelectedPlan = (
  name,
  age,
  employee,
  selectedPlan,
  dentalPlan,
  visionPlan,
  dependentEnrollment,
  employeeIndex,
  dependentIndex,
) => {
  if (!name || !age || !employee || !selectedPlan) {
    return 0;
  }

  // if there is a spouse, decrement dependentIndex to access the correct child cost
  dependentIndex = employee.spouse_age ? dependentIndex - 1 : dependentIndex;

  let costs = 0;
  const employeeMedicalCosts = selectedPlan.employee_costs[employeeIndex];
  const employeeDentalCosts = dentalPlan?.employee_costs && dentalPlan.employee_costs[employeeIndex];
  const employeeVisionCosts = visionPlan?.employee_costs && visionPlan.employee_costs[employeeIndex];

  if (name === "Spouse") {
    if (selectedPlan && dependentEnrollment.is_enrolling) {
      costs += employeeMedicalCosts?.spouse_cost || 0;
    }
    if (dentalPlan && dependentEnrollment.is_enrolling_dental) {
      costs += employeeDentalCosts?.spouse_cost || 0;
    }
    if (visionPlan && dependentEnrollment.is_enrolling_vision) {
      costs += employeeVisionCosts?.spouse_cost || 0;
    }
    return costs;
  } else {
    let costs = 0;
    if (selectedPlan && dependentEnrollment.is_enrolling) {
      if (employeeMedicalCosts?.child_cost_by_age) {
        costs += employeeMedicalCosts.child_cost_by_age?.find((child) => child.age === age)?.cost || 0;
      } else if (employeeMedicalCosts?.child_cost) {
        // segment-based pricing uses an array of child prices where the total price for all
        // children is divided equally between the first 3 children, and additional children are free
        costs += employeeMedicalCosts.child_cost[dependentIndex];
      }
    }
    // dental and vision costs are segmented so all the rates are the same
    // so we can just use the first one
    if (dentalPlan && dependentEnrollment.is_enrolling_dental) {
      costs += employeeDentalCosts?.child_cost ? employeeDentalCosts.child_cost[0] : 0;
    }
    if (visionPlan && dependentEnrollment.is_enrolling_vision) {
      costs += employeeVisionCosts?.child_cost ? employeeVisionCosts.child_cost[0] : 0;
    }
    return costs;
  }
};

// What the company pays for a dependent
export const calculateCompanyDependentCost = (
  contributionType,
  contribution,
  name,
  age,
  employee,
  selectedPlan,
  dentalPlan,
  visionPlan,
  dependentEnrollment,
  employeeIndex,
  dependentIndex,
  medicalPlans,
) => {
  if (contributionType == CONTRIBUTION_TYPES.percentage || contributionType === undefined) {
    const dependentCost = findDependentCostforSelectedPlan(
      name,
      age,
      employee,
      selectedPlan,
      dentalPlan,
      visionPlan,
      dependentEnrollment,
      employeeIndex,
      dependentIndex,
    );

    return dependentCost * (contribution.dependentPercentage / 100) || 0;
  }
  if (contributionType == CONTRIBUTION_TYPES.percent_of_base_plan) {
    const dependentCost = findDependentCostforSelectedPlan(
      name,
      age,
      employee,
      medicalPlans[0],
      dentalPlan,
      visionPlan,
      dependentEnrollment,
      employeeIndex,
      dependentIndex,
    );

    return dependentCost * (contribution.dependentPercentage / 100);
  }
  return 0;
};

// What the employee pays for a dependent
export const calculateEmployeeDependentCost = (
  employee,
  age,
  name,
  selectedPlan,
  dentalPlan,
  visionPlan,
  dependentEnrollment,
  contributionType,
  contribution,
  employeeIndex,
  dependentIndex,
  medicalPlans,
) => {
  let dependentCost;
  let companyCost;
  try {
    dependentCost = findDependentCostforSelectedPlan(
      name,
      age,
      employee,
      selectedPlan,
      dentalPlan,
      visionPlan,
      dependentEnrollment,
      employeeIndex,
      dependentIndex,
    );

    companyCost = calculateCompanyDependentCost(
      contributionType,
      contribution,
      name,
      age,
      employee,
      selectedPlan,
      dentalPlan,
      visionPlan,
      dependentEnrollment,
      employeeIndex,
      dependentIndex,
      medicalPlans,
    );

    if (isNaN(dependentCost)) throw new Error("Failed to calculate dependentCost for dependent");
    if (isNaN(companyCost)) throw new Error("Failed to calculate companyCost for dependent");
  } catch (error) {
    // Catch and log errors in price calculations
    logError(
      error,
      {
        name: "CostCalculations",
        action: "calculateEmployeeDependentCost",
        component: "EmployerApplication",
      },

      {
        dependentCost,
        companyCost,
        employee,
        age,
        name,
        selectedPlan,
        dentalPlan,
        visionPlan,
        dependentEnrollment,
        contributionType,
        contribution,
        employeeIndex,
        dependentIndex,
        medicalPlans,
      },
    );
  }
  return dependentCost - companyCost;
};

// Aggregate cost to company for all dependents (grouped for fixed contribution type)
export const calculateIndividualDependentCompanyCost = (selectedContribution, employee, index, selectedPlan) => {
  const employeeCost = selectedPlan?.employee_costs[index];

  if (!employeeCost) {
    return 0;
  }

  // Medical Plan Calculations
  // For fixed contribution we only calculate the contribution
  // towards medical so no need to calculate company costs
  // for dental and vision
  let dependentsPlanCost = 0;
  if (employee.spouse_enrollment && employee.spouse_enrollment.is_enrolling) {
    dependentsPlanCost += employeeCost?.spouse_cost;
  }
  employee.children_enrollment?.map((enrollment, childIndex) => {
    if (enrollment.is_enrolling && employeeCost?.child_cost) {
      dependentsPlanCost += employeeCost?.child_cost[childIndex];
    }
  });

  return Math.min(dependentsPlanCost, selectedContribution.dependentFixedAmount);
};

// Aggregate cost to employee for all dependents (grouped for fixed contribution type)
export const calculateIndividualDependentEmployeeCost = (selectedContribution, index, selectedPlan, dentalPlan, visionPlan, employee) => {
  const totalDependentCost = calculateIndividualDependentTotalCost(index, employee, selectedPlan, dentalPlan, visionPlan);

  const totalDependentCostForCompany = calculateIndividualDependentCompanyCost(selectedContribution, employee, index, selectedPlan);

  return totalDependentCost - totalDependentCostForCompany;
};

// Aggregate total cost for all dependents (grouped for fixed contribution type)
export const calculateIndividualDependentTotalCost = (index, employee, selectedPlan, dentalPlan, visionPlan) => {
  let dependentsTotalCost = 0;

  if (selectedPlan?.pricing_type === "segment") {
    dependentsTotalCost = calculateIndividualDependentTotalCostSegmented(index, employee, selectedPlan, dentalPlan, visionPlan);
  } else {
    // TODO: have to update this function to use spouse and children
    // rather than dependents
    employee.dependents.map((dependent, dependentIndex) => {
      dependentsTotalCost += findDependentCostforSelectedPlan(
        dependent.name,
        dependent.age,
        employee,
        selectedPlan,
        dentalPlan,
        visionPlan,
        {
          is_enrolling: dependent.is_enrolling,
          is_enrolling_dental: dependent.is_enrolling_dental,
          is_enrolling_vision: dependent.is_enrolling_vision,
        },

        index,
        dependentIndex,
      );
    });
  }

  return dependentsTotalCost;
};

const calculateIndividualDependentTotalCostSegmented = (index, employee, selectedPlan, dentalPlan, visionPlan) => {
  let dependentsTotalCost = 0;
  const pricingSegment = getPricingSegmentMedical(employee);
  const { employee_costs: employeeCosts } = selectedPlan;
  const { spouse_cost: spouseCost, child_cost: childCosts, family_cost: familyCost } = employeeCosts[index];

  if (pricingSegment === "employeeAndSpouse") {
    dependentsTotalCost += spouseCost;
  } else if (pricingSegment === "employeeAndChildren") {
    dependentsTotalCost += sum(childCosts);
  } else if (pricingSegment === "employeeAndFamily") {
    dependentsTotalCost += familyCost;
  }

  if (dentalPlan) {
    const pricingSegmentDental = getPricingSegmentAncillary(employee, "dental");
    const { employee_costs: employeeCostsDental } = dentalPlan;
    const { spouse_cost: spouseCostDental, child_cost: childCostsDental, family_cost: familyCostDental } = employeeCostsDental[index];

    if (pricingSegmentDental === "employeeAndSpouse") {
      dependentsTotalCost += spouseCostDental;
    } else if (pricingSegmentDental === "employeeAndChildren") {
      dependentsTotalCost += sum(childCostsDental);
    } else if (pricingSegmentDental === "employeeAndFamily") {
      dependentsTotalCost += familyCostDental;
    }
  }

  if (visionPlan) {
    const pricingSegmentVision = getPricingSegmentAncillary(employee, "vision");
    const { employee_costs: employeeCostsVision } = visionPlan;
    const { spouse_cost: spouseCostVision, child_cost: childCostsVision, family_cost: familyCostVision } = employeeCostsVision[index];

    if (pricingSegmentVision === "employeeAndSpouse") {
      dependentsTotalCost += spouseCostVision;
    } else if (pricingSegmentVision === "employeeAndChildren") {
      dependentsTotalCost += sum(childCostsVision);
    } else if (pricingSegmentVision === "employeeAndFamily") {
      dependentsTotalCost += familyCostVision;
    }
  }
  return dependentsTotalCost;
};
